import * as queryString from 'query-string';
import { MicrobrandingParams, URLParameters } from '../types';
import { Location as HistoryLocation } from 'history';

export type CoreApplication =
  | 'ace'
  | 'access'
  | 'admin'
  | 'atlas'
  | 'bitbucket'
  | 'cac'
  | 'community'
  | 'compass'
  | 'confluence'
  | 'dia'
  | 'dac'
  | 'experts'
  | 'itap-uat-backend'
  | 'jac'
  | 'jira'
  | 'loom'
  | 'jira-align'
  | 'megamanzero'
  | 'mac'
  | 'mpac'
  | 'ondemand'
  | 'opsgenie'
  | 'pollinator' // for functional testing
  | 'prt' // for Primary Refresh Token testing
  | 'profile'
  | 'manage-profile'
  | 'rovo'
  | 'sac'
  | 'trac'
  | 'sourcetree'
  | 'start'
  | 'statuspage'
  | 'teamcentral'
  | 'trello'
  | 'uac'
  | 'wac'
  | 'support';

export type ApplicationResolutionSource = 'application-param' | 'continue-param';

export type ApplicationData = {
  coreApplication: CoreApplication;
  experience?: string;
  resolvedFrom: ApplicationResolutionSource;
} & ApplicationMetadata;

export interface ApplicationMetadata {
  name: string;
  useInLoginPrompt?: boolean;
}

export type ApplicationsMetadata = { [P in CoreApplication]: ApplicationMetadata };

export const applicationsMetadata: ApplicationsMetadata = {
  admin: {
    name: 'Adminhub',
    useInLoginPrompt: false,
  },
  access: {
    name: 'Adminhub',
    useInLoginPrompt: false,
  },
  ace: {
    name: 'Atlassian Community Events',
    useInLoginPrompt: false,
  },
  atlas: {
    name: 'Atlas',
    useInLoginPrompt: true,
  },
  bitbucket: {
    name: 'Bitbucket',
    useInLoginPrompt: true,
  },
  community: {
    name: 'Community',
    useInLoginPrompt: true,
  },
  cac: {
    name: 'Documentation',
    useInLoginPrompt: true,
  },
  dac: {
    name: 'Developer Portal',
    useInLoginPrompt: true,
  },
  dia: {
    name: 'Dia',
    useInLoginPrompt: true,
  },
  experts: {
    name: 'Partner Program',
    useInLoginPrompt: true,
  },
  'itap-uat-backend': {
    name: 'itap-uat-backend',
    useInLoginPrompt: false,
  },
  jac: {
    name: 'Atlassian Bug Reporting & Feature Requests',
  },
  mpac: {
    name: 'Marketplace',
    useInLoginPrompt: true,
  },
  profile: {
    name: 'Atlassian',
  },
  prt: {
    name: 'Primary Refresh Token Test',
  },
  'manage-profile': {
    name: 'Atlassian account',
    useInLoginPrompt: false,
  },
  sac: {
    name: 'Support',
    useInLoginPrompt: true,
  },
  trac: {
    name: 'TRAC',
  },
  teamcentral: {
    name: 'Team Central',
    useInLoginPrompt: false,
  },
  trello: {
    name: 'Trello',
    useInLoginPrompt: true,
  },
  mac: {
    name: 'My Atlassian',
    useInLoginPrompt: true,
  },
  jira: {
    name: 'Jira',
    useInLoginPrompt: true,
  },
  loom: {
    name: 'Loom',
    useInLoginPrompt: true,
  },
  'jira-align': {
    name: 'Jira Align',
    useInLoginPrompt: true,
  },
  confluence: {
    name: 'Confluence',
    useInLoginPrompt: true,
  },
  ondemand: {
    name: 'Atlassian Cloud',
    useInLoginPrompt: false,
  },
  opsgenie: {
    name: 'Opsgenie',
    useInLoginPrompt: true,
  },
  rovo: {
    name: 'Rovo',
    useInLoginPrompt: false,
  },
  start: {
    name: 'Start',
    useInLoginPrompt: false,
  },
  megamanzero: {
    name: 'MegamanZero',
    useInLoginPrompt: false,
  },
  pollinator: {
    // for functional testing
    name: 'Pollinator',
    useInLoginPrompt: false,
  },
  wac: {
    name: 'Atlassian',
    useInLoginPrompt: false,
  },
  compass: {
    name: 'Compass',
    useInLoginPrompt: true,
  },
  sourcetree: {
    name: 'Sourcetree',
    useInLoginPrompt: false,
  },
  statuspage: {
    name: 'Statuspage',
  },
  uac: {
    name: 'UAC',
    useInLoginPrompt: false,
  },
  support: {
    name: 'Support',
    useInLoginPrompt: true,
  },
};

export type ApplicationLookupInputs = {
  application: string | null;
  continueUrl: string | null;
};

export function lookupApplication(searchParams: URLSearchParams): ApplicationData | null {
  return lookupApplicationFromValues({
    application: searchParams.get('application'),
    continueUrl: searchParams.get('continue'),
  });
}

export function lookupApplicationFromValues({
  application,
  continueUrl,
}: ApplicationLookupInputs): ApplicationData | null {
  // An explicit application, even empty or invalid, must block the continue fallback.
  const applicationData =
    application !== null
      ? applicationDataFromApplication(application)
      : applicationDataFromContinueUrl(continueUrl);
  const resolvedFrom: ApplicationResolutionSource =
    application !== null ? 'application-param' : 'continue-param';

  const { coreApplication, experience } = applicationData;

  if (coreApplication) {
    const appData = applicationsMetadata[coreApplication];
    if (appData) {
      return {
        coreApplication,
        ...(experience && { experience }),
        resolvedFrom,
        ...appData,
      };
    }
  }

  return null;
}

export const cobrandingURLParams = (
  location: Location | HistoryLocation,
  productHint: string | undefined
) => {
  const params: URLParameters = queryString.parse(location.search);
  const allParams =
    productHint !== undefined && productHint !== null
      ? { ...params, productHint: productHint }
      : { ...params };

  const filteredCobrandingParams = Object.keys(allParams)
    .filter(key => ['continue', 'application', 'display', 'productHint'].includes(key))
    .reduce((o, key) => {
      o[key] = allParams[key];
      return o;
    }, {});

  const cobrandingURLParams =
    filteredCobrandingParams && Object.keys(filteredCobrandingParams).length
      ? { ...filteredCobrandingParams }
      : '';
  return cobrandingURLParams;
};

/*
 * We should kill it
 */
export function getMicrobrandingFromUrl(location: Location | HistoryLocation): MicrobrandingParams {
  const params: URLParameters = queryString.parse(location.search);
  return Object.keys(params)
    .filter(key => ['application', 'continue', 'tenant'].includes(key))
    .reduce((o, key) => {
      o[key] = params[key];
      return o;
    }, {});
}

/**
 * Exported for testing only.
 *
 * Paths that don't look like they belong to Jira (or Confluence) on *.atlassian.net.
 * https://stash.atlassian.com/projects/EDGE/repos/atlassian-proxy/browse/nginx/lua/endpoints.lua#271
 */
export const NON_JIRA_PATHS = [
  '/admin',
  '/login',
  '/logout',
  '/impersonate',
  '/home',
  '/people',
  '/trusted-admin',
  '/forge-apps',
];

/**
 * Determine the cobranding application from the `?continue=...` URL param as a last resort.
 *
 * **This code should _only_ be relied upon for situations where passing an `?application=...`
 * param or product hint is not possible**, such as within Slack's direct initiate flow. In OIDC
 * flows applications should pass a product hint, and in all other flows applications should pass
 * an `?application=...` param to id.a.c.
 *
 * Ordinarily, coreApplication and experience would be derived from the `application` param, where
 * the value contains the two separated by the delimeter '--', like so:
 * ...?application=foo--bar
 *                 ^^^  ^^^
 *                 |    |
 *                 |    experience
 *                 |
 *                 coreApplication
 *
 * In this function, we fall back to deriving them from the continue URL on a case-by-case basis,
 * **arbitrarily**.
 *
 * For this reason, it is not a 'normal' entry point for external contributors. See comments below.
 */
function applicationDataFromContinueUrl(continueParam: string | null): {
  coreApplication: CoreApplication | null;
  experience: string | null;
} {
  if (!continueParam)
    return {
      coreApplication: null,
      experience: null,
    };

  try {
    const continueUrl = new URL(continueParam, window.location.origin);

    const { hostname, pathname, searchParams } = continueUrl;

    /**
     * NOTE: Contract for External Contributors. Must Read.
     *
     * This is not a self-serve entry point for cobranding.
     *
     * Changes require consent and approval from team Kirby. Team Kirby reserves the right to
     * decline. It's strongly encouraged to seek guidance for your use case in #id-kirby prior
     * to raising PRs.
     *
     * When adding new custom rules & cases:
     *  - Always feature gate
     *  - Always add to the end of the condition block (adding to start can change existing
     *  behaviour)
     *  - Never override existing behaviour without express consent of its original implementor
     */
    if (['bitbucket.org', 'integration.bb-inf.net'].includes(hostname)) {
      return { coreApplication: 'bitbucket', experience: null };
    } else if (['.opsgenie.com', '.opsgeni.us'].some(domain => hostname.endsWith(domain))) {
      return { coreApplication: 'opsgenie', experience: null };
    } else if (['manage.statuspage.io', 'manage.statuspagestaging.com'].includes(hostname)) {
      return { coreApplication: 'statuspage', experience: null };
    } else if (['trello.com', 'trellis.coffee'].includes(hostname)) {
      return { coreApplication: 'trello', experience: null };
    } else if (
      ['atlassian.com', 'www.atlassian.com', 'wac.stg.internal.atlassian.com'].includes(hostname)
    ) {
      return { coreApplication: 'wac', experience: null };
    } else if (['team.atlassian.com', 'team.stg.atlassian.com'].includes(hostname)) {
      return { coreApplication: 'atlas', experience: null };
    } else if (
      ['.atlassian.net', 'atlassian-fex.net', '.jira-dev.com'].some(domain =>
        hostname.endsWith(domain)
      )
    ) {
      const unwrappedContinueUrl =
        pathname === '/login' && searchParams.get('dest-url')
          ? new URL(searchParams.get('dest-url')!, continueUrl)
          : continueUrl;
      const { pathname: unwrappedPathname } = unwrappedContinueUrl;

      if (unwrappedPathname === '/wiki' || unwrappedPathname.startsWith('/wiki/')) {
        return { coreApplication: 'confluence', experience: null };
      } else if (unwrappedPathname === '/compass' || unwrappedPathname.startsWith('/compass/')) {
        return { coreApplication: 'compass', experience: null };
      } else if (
        // Root could be Jira or Conflience
        unwrappedPathname !== '/' &&
        !NON_JIRA_PATHS.some(
          prefix => unwrappedPathname === prefix || unwrappedPathname.startsWith(`${prefix}/`)
        )
      ) {
        return { coreApplication: 'jira', experience: null };
      }
    }
  } catch (error) {
    // Malformed URL, ignore
  }

  return {
    coreApplication: null,
    experience: null,
  };
}

function applicationDataFromApplication(application: string): {
  coreApplication: CoreApplication | null;
  experience: string | null;
} {
  try {
    const [coreApplication, experience] = application.split('--');
    if (coreApplication && isValidApplication(coreApplication)) {
      return { coreApplication, experience };
    }
  } catch (error) {
    // Malformed query parameter, ignore
  }
  return { coreApplication: null, experience: null };
}

export function isTrelloDesktop(): boolean {
  const userAgent = window.navigator.userAgent;
  return userAgent.includes('TrelloDesktop');
}

export const getApplicationName = (app?: CoreApplication) =>
  app && applicationsMetadata[app] ? applicationsMetadata[app].name : 'Atlassian';

const isValidApplication = (application: string): application is CoreApplication =>
  Object.keys(applicationsMetadata).includes(application);

export function replaceQueryParam(paramName: string, paramValue: string): string {
  const queryParams = new URLSearchParams(window.location.search);
  queryParams.set(paramName, paramValue);
  const queryString = queryParams.toString();
  return queryString ? `?${queryString}` : '';
}
